import React from 'react';
import { Modal } from 'react-bootstrap';
import Button from 'shared/components/button/Button';
import { formatDateWithMoment } from 'utils';
import Switch from 'shared/components/switch/Switch';
import style from './../architecture.module.css';
import {
  ActionTypes,
  Layouts,
  BlockTypes,
  defaultModalState,
  daysAbbreviation,
  ProgramTypes,
  RECURRENCE_DAYS,
  RECURRENCE_PATTERN_LIST,
  RECURRENCE_PATTERN,
} from '../ChannelArchitecture.d';
import { cAModalReducer } from '../reducers/schedules.reducer';
import OpTimeInput from 'op2mise-react-widgets/OpTimeInput/OpTimeInput';
import {
  displayLayoutInput,
  getBlockName,
  updateBlockReference,
  validateProgrammeBlockData,
} from '../utils/architecture.utils';
import { getMondayOfTheWeek, addDaysToDate } from 'utils';
import moment from 'moment';
import findRelatedBlocks from '../helper/findRelatedBlocks';
import { Constants } from '../helper/constants';
import { getMondayZeroToSixSchedules } from '../helper/getMondayZeroToSixSchedules';
import { reconstructBlockName } from '../utils/architecture.utils';
import { useProgramsStore } from '../store/programsStore';
import OpSelect from 'op2mise-react-widgets/OpSelect/OpSelect';
import getEditBlockState from '../helper/getEditBlockState';
import DailyRecurrencePatternComponent from '../components/DailyRecurrencePatternComponent';
import { buildWeeklyRecurrence } from '../utils/recurrence.utils';

/* eslint-disable */
function ModifyBlock({
  schedules,
  dispatchSchedules,
  handleCloseModal,
  blockData,
  numberOfWeeks,
  modalType,
  referenceList,
  isBroadcast,
  setIsDirty,
}) {
  const { programs, getSeriesInfo, getTitleGroupInfo } = useProgramsStore((state) => state);
  const { daysOfWeek } = Constants;
  const { titleGroupList, seriesList } = programs;
  const isModalSingleRepeatType = modalType === ActionTypes.REPEAT;
  const isModalMultipleRepeatType = modalType === ActionTypes.MULTIPLEREPEAT;
  const isModalOfRepeatType = isModalSingleRepeatType || isModalMultipleRepeatType;

  const [recurrencePattern, setRecurrencePattern] = React.useState({
    id: RECURRENCE_PATTERN_LIST[0].id,
    name: RECURRENCE_PATTERN_LIST[0].name,
    everyWeekInterval: 1,
    days: RECURRENCE_DAYS.map((day) => day.id),
  });
  const [modalState, dispatchModalState] = React.useReducer(cAModalReducer, defaultModalState);

  // Input Component for Modify Block Dialog
  const InputComponent = ({ label, input, gap }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: gap ?? 20,
      }}
    >
      {label && <p>{label}</p>}
      {input}
    </div>
  );

  // Set the block data to be modified for the Modify Block Dialog
  React.useEffect(() => {
    const newValues = getEditBlockState({
      blockData,
      modalType,
      referenceList,
      schedules,
      seriesList,
      titleGroupList,
    });

    if (newValues) {
      dispatchModalState({
        action: 'changeValue',
        newValues: {
          ...newValues,
          disablePlacement: [
            ProgramTypes.REPEAT,
            ProgramTypes.MOVIES,
            ProgramTypes.PLACEHOLDER,
          ].includes(newValues.type),
        },
      });
    }
  }, [blockData]);

  const canAddDay = (linkedProgram, temp) => {
    const canAddWeek = linkedProgram.week < numberOfWeeks;
    const canAddDay = linkedProgram.dayOfWeek == 7 ? canAddWeek : true;
    return canAddDay;
  };

  const handleErrorMessage = (message) => {
    dispatchModalState({
      action: 'changeValue',
      newValues: {
        errorMessage: message,
      },
    });
  };

  const getLinkReferenceInfo = (data) => {
    return schedules.programmes.find((o) => o.blockName === data.blockName);
  };

  const getTargetBlockReference = ({
    isRepeatType,
    originalLinkName,
    originalStartTime,
    relatedBlocks,
    updatedLinkName,
    updatedStartTime,
  }) => {
    // Check what actually changed
    const hasLinkReferenceChanged = updatedLinkName !== originalLinkName;
    const isStartTimeChanged =
      formatDateWithMoment(originalStartTime, 'HH:mm:ss', 'HH:mm:ss') !==
      formatDateWithMoment(updatedStartTime, 'HH:mm:ss', 'HH:mm:ss');

    let finalBlockReference = updatedLinkName; // Default for CREATE/REPEAT

    const shouldUpdateReference = relatedBlocks.some(
      (block) => block.blockName === originalLinkName,
    );

    if (!shouldUpdateReference) return finalBlockReference;

    // Apply specific rules for UPDATE action
    if (modalType === ActionTypes.UPDATE && !isRepeatType) {
      if (hasLinkReferenceChanged) {
        // Rule: If Reference changed (regardless of time), use reference directly
        finalBlockReference = updatedLinkName;
      } else if (isStartTimeChanged) {
        // Rule: If ONLY Time changed, calculate new reference
        finalBlockReference = updateBlockReference(
          originalLinkName,
          formatDateWithMoment(updatedStartTime, 'HH:mm:ss', 'HH:mm:ss'),
          schedules.programmes,
        );
      } else {
        // Rule: Neither changed, keep original
        finalBlockReference = originalLinkName;
      }
    }

    return finalBlockReference;
  };

  const handleUpdateByLinkedBlocks = (newBlockData, targetBlock, relatedBlocks) => {
    let updated = [];

    const allRelatedBlocks = [targetBlock, ...relatedBlocks];

    if (relatedBlocks.length) {
      updated = relatedBlocks.map((block) => {
        // Check if target block is a non-Repeat Type
        if (targetBlock.type !== ProgramTypes.REPEAT) {
          // Update following:
          // 1. Start time and end time
          // 2. Block name according to the new start time
          // 3. Block reference according to the new start time
          // 4. Series, Season and TitleGroup according to the new block data
          // 5. Type according to the new block data
          // Basically everything except the week and day of week and block id
          if (block.type === 'TitleGroup' || block.type === targetBlock.type) {
            return {
              ...block,
              layout: newBlockData.layout,
              startTime: newBlockData.startTime,
              endTime: newBlockData.endTime,
              blockName: getBlockName(block.dayOfWeek, newBlockData.startTime),
              blockReference: updateBlockReference(
                block.blockReference,
                newBlockData.startTime,
                allRelatedBlocks,
              ),
              seriesID: newBlockData.seriesID,
              seriesName: newBlockData.seriesName,
              seasonID: newBlockData.seasonID,
              seasonName: newBlockData.seasonName,
              titleGroupID: newBlockData.titleGroupID,
              titleGroupName: newBlockData.titleGroupName,
              type: newBlockData.type,
            };
          } else {
            // If block is repeat type, udpate the following:
            // 1. Series, Season and TitleGroup according to the new block data
            // Everything else remains the same
            return {
              ...block,
              layout: newBlockData.layout,
              seriesID: newBlockData.seriesID,
              seriesName: newBlockData.seriesName,
              seasonID: newBlockData.seasonID,
              seasonName: newBlockData.seasonName,
              titleGroupID: newBlockData.titleGroupID,
              titleGroupName: newBlockData.titleGroupName,
              blockReference: updateBlockReference(
                block.blockReference,
                newBlockData.startTime,
                allRelatedBlocks,
              ),
            };
          }
        } else {
          // If target block is a repeat
          if (block.type === newBlockData.type && newBlockData.type === ProgramTypes.REPEAT) {
            // If block type is repeat, update the following:
            // 1. Start time and end time
            // - Update the start time and end time of the block if following conditions are met:
            //   - block start and target start time are the same as well as the end time, this is to make sure that we are retrieving the correct blocks of the same time slot throughout the week
            // 2. Block name according to the new start time
            // 3. Series, Seasons, TitleGroup according to the new block data
            // 4. Genre, max count and available duration according to the new block data
            // rest should remain as the block's data
            return {
              ...block,
              startTime: newBlockData.startTime,
              endTime: newBlockData.endTime,
              blockName: getBlockName(block.dayOfWeek, newBlockData.startTime),
              layout: newBlockData.layout,
              seriesID: newBlockData.seriesID,
              seriesName: newBlockData.seriesName,
              seasonID: newBlockData.seasonID,
              seasonName: newBlockData.seasonName,
              titleGroupID: newBlockData.titleGroupID,
              titleGroupName: newBlockData.titleGroupName,
              genre: newBlockData.genre,
              maxCount: newBlockData.maxCount,
              availableDuration: newBlockData.availableDuration,
            };
          } else {
            // If block type is not repeat, update the following:
            // 1. Series, Season and TitleGroup according to the new block data
            // Rest remain as is
            return {
              ...block,
              layout: newBlockData.layout,
              seriesID: newBlockData.seriesID,
              seriesName: newBlockData.seriesName,
              seasonID: newBlockData.seasonID,
              seasonName: newBlockData.seasonName,
              titleGroupID: newBlockData.titleGroupID,
              titleGroupName: newBlockData.titleGroupName,
            };
          }
        }
      });
    }
    return updated;
  };

  const handleMutipleUpdate = (newBlockData, targetBlock, relatedBlocks) => {
    let updated = [];

    const allRelatedBlocks = [targetBlock, ...relatedBlocks];

    if (relatedBlocks.length) {
      updated = relatedBlocks.map((block) => {
        // Check if target block is a non-Repeat Type
        if (targetBlock.type !== ProgramTypes.REPEAT) {
          // Update following:
          // 1. Start time and end time
          // 2. Block name according to the new start time
          // 3. Block reference according to the new start time
          // 4. Series, Season and TitleGroup according to the new block data
          // 5. Type according to the new block data
          // Basically everything except the week and day of week and block id
          if (block.type === 'TitleGroup' || block.type === targetBlock.type) {
            return {
              ...block,
              startTime:
                moment(targetBlock.startTime, 'HH:mm:ss').format('HH:mm:ss') === block.startTime
                  ? newBlockData.startTime
                  : block.startTime,
              endTime:
                moment(targetBlock.endTime, 'HH:mm:ss').format('HH:mm:ss') === block.endTime
                  ? newBlockData.endTime
                  : block.endTime,
              blockName: getBlockName(block.dayOfWeek, newBlockData.startTime),
              blockReference: updateBlockReference(
                block.blockReference,
                newBlockData.startTime,
                allRelatedBlocks,
              ),
              layout: newBlockData.layout,
              seriesID: newBlockData.seriesID,
              seriesName: newBlockData.seriesName,
              seasonID: newBlockData.seasonID,
              seasonName: newBlockData.seasonName,
              titleGroupID: newBlockData.titleGroupID,
              titleGroupName: newBlockData.titleGroupName,
              type: newBlockData.type,
            };
          } else {
            // If block is repeat type, udpate the following:
            // 1. Series, Season and TitleGroup according to the new block data
            // Everything else remains the same
            return {
              ...block,
              layout: newBlockData.layout,
              seriesID: newBlockData.seriesID,
              seriesName: newBlockData.seriesName,
              seasonID: newBlockData.seasonID,
              seasonName: newBlockData.seasonName,
              titleGroupID: newBlockData.titleGroupID,
              titleGroupName: newBlockData.titleGroupName,
              blockReference: updateBlockReference(
                block.blockReference,
                newBlockData.startTime,
                allRelatedBlocks,
              ),
            };
          }
        } else {
          // If target block is a repeat
          if (block.type === newBlockData.type && newBlockData.type === ProgramTypes.REPEAT) {
            // If block type is repeat, update the following:
            // 1. Start time and end time
            // - Update the start time and end time of the block if following conditions are met:
            //   - block start and target start time are the same as well as the end time, this is to make sure that we are retrieving the correct blocks of the same time slot throughout the week
            // 2. Block name according to the new start time
            // 3. Series, Seasons, TitleGroup according to the new block data
            // 4. Genre, max count and available duration according to the new block data
            // rest should remain as the block's data
            return {
              ...block,
              startTime:
                moment(targetBlock.startTime, 'HH:mm:ss').format('HH:mm:ss') === block.startTime
                  ? newBlockData.startTime
                  : block.startTime,
              endTime:
                moment(targetBlock.endTime, 'HH:mm:ss').format('HH:mm:ss') === block.endTime
                  ? newBlockData.endTime
                  : block.endTime,
              blockName: getBlockName(block.dayOfWeek, newBlockData.startTime),
              layout: newBlockData.layout,
              seriesID: newBlockData.seriesID,
              seriesName: newBlockData.seriesName,
              seasonID: newBlockData.seasonID,
              seasonName: newBlockData.seasonName,
              titleGroupID: newBlockData.titleGroupID,
              titleGroupName: newBlockData.titleGroupName,
              genre: newBlockData.genre,
              maxCount: newBlockData.maxCount,
              availableDuration: newBlockData.availableDuration,
            };
          } else {
            // If block type is not repeat, update the following:
            // 1. Series, Season and TitleGroup according to the new block data
            // Rest remain as is
            return {
              ...block,
              layout: newBlockData.layout,
              seriesID: newBlockData.seriesID,
              seriesName: newBlockData.seriesName,
              seasonID: newBlockData.seasonID,
              seasonName: newBlockData.seasonName,
              titleGroupID: newBlockData.titleGroupID,
              titleGroupName: newBlockData.titleGroupName,
            };
          }
        }
      });
    }
    return updated;
  };

  const handleModifyBlock = (props) => {
    // 1. Validation
    const validationError = validateProgrammeBlockData({
      startTime: props.startTime,
      endTime: props.endTime,
      hasLink: modalState.hasLink,
      linkReference: modalState.linkReference,
      type: props.type,
      layout: modalState.layout,
    });

    if (validationError) return handleErrorMessage(validationError);

    // 2. Data Preparation
    const useLinkedMetadata =
      props.type === ProgramTypes.REPEAT && modalType === ActionTypes.CREATE;
    const isRepeatType = props.type === ProgramTypes.REPEAT;

    const selectedReference = referenceList.find(
      (o) => o.blockName === modalState.linkReference?.blockName,
    );

    const linkedProgram = selectedReference ? getLinkReferenceInfo(selectedReference) : {};

    const selectedTitleGroup =
      titleGroupList.find((o) => o.name === linkedProgram?.titleGroupName) || null;

    const currentProgram = schedules.programmes?.find((o) => o.id === props.id) || {};

    const period = {
      startDate: getMondayOfTheWeek(props.startTime),
      endDate: addDaysToDate(getMondayOfTheWeek(props.endTime), 6),
    };

    const seriesSource = useLinkedMetadata ? linkedProgram : modalState.series;

    const seriesName = useLinkedMetadata ? linkedProgram?.seriesName : seriesSource?.name;

    const titleGroupName = useLinkedMetadata
      ? linkedProgram?.titleGroupName
      : props?.titleGroupName;

    const relatedBlocks = findRelatedBlocks(blockData, schedules.programmes, {
      includeRepeats: true,
      includeSelected: false,
      requireExactName: Boolean(seriesName || titleGroupName),
      searchEntireSchedule: false,
    });

    // 3. Construct Payload
    const payload = {
      ...currentProgram,
      id: blockData.id,
      genre: modalState.genre,
      maxCount: modalState.programMaxCount,
      availableDuration: modalState.programAvailableDuration,
      week: props.week,
      dayOfWeek: props.dayOfWeek.id,
      startTime: formatDateWithMoment(props.startTime, 'HH:mm:ss', 'HH:mm:ss'),
      endTime: formatDateWithMoment(props.endTime, 'HH:mm:ss', 'HH:mm:ss'),
      layout: modalState.layout,
      link: !!modalState.hasLink,
      sequential: modalState.sequential,
      type: props.type.replace(' ', ''),
      seasonID: props.seasonID,
      seasonName: modalState.type === ProgramTypes.SERIES ? modalState.seasonName : null,
      // Metadata Resolution
      series: useLinkedMetadata ? linkedProgram?.seriesID : seriesSource,
      seriesID: useLinkedMetadata ? linkedProgram?.seriesID : seriesSource?.id,
      seriesName,
      titleGroupName,
      titleGroupID: isRepeatType ? selectedTitleGroup?.id : props?.titleGroupID,
      blockReference: getTargetBlockReference({
        updatedLinkName: modalState.linkReference?.blockName,
        originalLinkName: blockData.blockReference,
        updatedStartTime: props.startTime,
        originalStartTime: blockData.startTime,
        isRepeatType,
        relatedBlocks,
      }),
      blockName: getBlockName(
        props.dayOfWeek.id,
        formatDateWithMoment(props.startTime, 'HH:mm', 'HH:mm:ss'),
      ),
    };

    // 4. ACTION: UPDATE
    if (modalType === ActionTypes.UPDATE) {
      const titleGroupInfo = getTitleGroupInfo(modalState.titleGroup?.name);
      const seriesInfo = getSeriesInfo(modalState.series?.name, modalState.seasons?.id);
      const hasExistingTitle = blockData.seriesName || blockData.titleGroupName;

      // Determine if we update multiple blocks or just the linked ones
      const isMultiUpdate = (seriesInfo || titleGroupInfo) && hasExistingTitle;
      const additionalUpdates = isMultiUpdate
        ? handleMutipleUpdate(payload, blockData, relatedBlocks)
        : handleUpdateByLinkedBlocks(payload, blockData, relatedBlocks);

      // Combine current block with related updates
      const eventsToSave = [...additionalUpdates, { ...payload, week: blockData.week }];

      // Handle Broadcast specific clone
      const cloneUpdate =
        isBroadcast && blockData.cloneData
          ? [
              {
                ...payload,
                id: blockData.cloneData.id,
                week: blockData.cloneData.week,
              },
            ]
          : [];

      dispatchSchedules({
        actionType: ActionTypes.UPDATE,
        payload: { items: [...eventsToSave, ...cloneUpdate] },
      });
    }

    // 5. ACTION: CREATE
    else {
      const isRecurrence = Object.keys(recurrencePattern).length > 0;

      // NOTE: isRecurrence always ever returns as true as a recurrencePattern is always selected now/
      // TODO: remove this code path as it introduces unnecessary complexity
      if (!isRecurrence) {
        // --- Insert After Link ---
        const parentBlock = schedules.programmes.find(
          (o) => reconstructBlockName(o.blockName) === reconstructBlockName(payload.blockReference),
        );

        if (!parentBlock) return handleErrorMessage('Link reference is required.');

        const isAddDay = parentBlock.endTime === '00:00:00';
        if (isAddDay && !canAddDay(parentBlock, payload))
          return handleErrorMessage('After link reference is out of bounds');

        // Calculate Next Day/Week logic
        const programDay = parseInt(parentBlock.dayOfWeek);
        const nextDay = isAddDay ? (programDay === 7 ? 1 : programDay + 1) : programDay;
        const nextWeek =
          isAddDay && programDay === 7 ? parseInt(parentBlock.week) + 1 : parentBlock.week;

        let newBlock = {
          ...payload,
          id: window.crypto.randomUUID(),
          week: nextWeek,
          dayOfWeek: nextDay,
          startTime: parentBlock.endTime,
          blockName:
            daysAbbreviation[nextDay - 1] + moment(payload.startTime, 'HH:mm:ss').format('HH:mm'),
        };

        dispatchSchedules({
          actionType: ActionTypes.CREATE,
          payload: { item: getMondayZeroToSixSchedules([newBlock], period, isBroadcast) },
        });
      } else {
        // --- Insert with Recurrence ---
        const { days, id: recurrenceId, } = recurrencePattern;
        // Weekly
        if (!days.length) return handleErrorMessage('Please select the day(s) of the week');

        // NOTE: everyWeekInterval may be unnecessary from here on out, consider removing it
        const newEvents = buildWeeklyRecurrence({
          recurrence: {
            days,
            pattern: recurrenceId,
            weekStart: props.week,
            numberOfWeeks,
          },
          block: payload,
          modal: {
            type: modalType,
            state: modalState,
          },
          schedules,
          blockData,
          isBroadcast,
        });

        if (newEvents.length) {
          dispatchSchedules({
            actionType: ActionTypes.CREATE,
            payload: { item: getMondayZeroToSixSchedules(newEvents, period, isBroadcast) },
          });
        }
      }
    }

    // 6. Cleanup
    if (!modalState.errorMessage) setIsDirty(true);
    handleCloseModal();
  };

  const changeType = (args) => {
    const disablePlacement = [
      ProgramTypes.REPEAT,
      ProgramTypes.MOVIES,
      ProgramTypes.PLACEHOLDER,
    ].includes(args.name);

    const newLayoutValue = disablePlacement ? '' : modalState.layout || Layouts.SEQUENTIAL;

    dispatchModalState({
      action: 'changeType',
      type: args.name,
      disablePlacement,
      layout: displayLayoutInput(newLayoutValue),
    });
    changeForm('series', null);
    changeForm('seasons', null);
    changeForm('seasonName', null);

    // Reset recurrence pattern to Strip mode if the user changes to a non-repeat type
    if (args.name !== ProgramTypes.REPEAT && recurrencePattern.id === RECURRENCE_PATTERN.STACK) {
      setRecurrencePattern((prevState) => ({
        ...prevState,
        id: RECURRENCE_PATTERN_LIST[0].id,
        name: RECURRENCE_PATTERN_LIST[0].name,
      }));
    }
  };

  const changeForm = (attribute, value) => {
    dispatchModalState({
      action: 'changeValue',
      newValues: {
        [attribute]: value,
      },
    });
  };

  const handleTitleChange = (linkReference) => {
    if (!linkReference || modalState.type !== ProgramTypes.REPEAT) return;

    const info = getLinkReferenceInfo(linkReference);
    const seriesInfo = seriesList.find((o) => o.name === info?.seriesName);
    const titleGroupInfo = titleGroupList.find((o) => o.name === info?.titleGroupName);

    if (seriesInfo) {
      const seasonInfo = info.seasonID
        ? seriesInfo.seasons.find((season) => Number(season.id) === Number(info.seasonID))
        : null;

      changeForm('series', seriesInfo);
      changeForm('titleGroup', null);
      changeForm('seasons', seasonInfo);
      changeForm('seasonName', seasonInfo?.name || null);
    }

    if (titleGroupInfo) {
      changeForm('titleGroup', titleGroupInfo);
      changeForm('series', null);
      changeForm('seasons', null);
    }
  };

  const recomputeBlockEndTimeFromLinkReference = (linkReference) => {
    if (!linkReference?.blockName) return;

    // find the linked program in the current schedule that matches the link reference's block name
    const linkedProgram = schedules.programmes.find(
      (o) =>
        reconstructBlockName(o?.blockName) ===
        reconstructBlockName(linkReference.blockName),
    );

    if (!linkedProgram) return;

    // compute new endTime from linked program duration
    const linkReferenceDuration = moment(linkedProgram.endTime, 'HH:mm:ss').diff(
      moment(linkedProgram.startTime, 'HH:mm:ss'),
      'milliseconds',
    );
    const newBlockEndTime = moment(modalState.startTime, 'HH:mm:ss')
      .add(linkReferenceDuration, 'milliseconds')
      .toDate();

    // set new endTime in the form state
    changeForm('endTime', newBlockEndTime);
  };

  const changeLinkReference = (e) => {
    const linkReference = e.target.itemData;

    dispatchModalState({
      action: 'changeValue',
      newValues: {
        linkReference: linkReference,
      },
    });

    recomputeBlockEndTimeFromLinkReference(linkReference);
    handleTitleChange(linkReference);
  };

  const handleToggleLink = (value) => {
    dispatchModalState({
      action: 'changeValue',
      newValues: {
        hasLink: value,
        disableStartTime: false,
        linkReference: null,
      },
    });
  };

  const modalHeaderTitle = () => {
    switch (modalType) {
      case 'CREATE':
        return 'New';
      case 'UPDATE':
        return 'Modify';
      case 'MULTIPLEREPEAT':
      case 'REPEAT':
        return ProgramTypes.REPEAT;
    }
  };

  const typeList =
    Object.keys(recurrencePattern).length !== 0
      ? BlockTypes
      : BlockTypes.filter((o) => o.name !== ProgramTypes.REPEAT);
  return (
    <Modal
      show={true}
      onHide={handleCloseModal}
      style={{ '--bs-modal-width': '1030px' }}
      enforceFocus={false}
    >
      <Modal.Body>
        <div style={{ padding: '30px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 7,
              fontSize: '20px',
            }}
          >
            {modalHeaderTitle()} Block
            {(modalType == ActionTypes.UPDATE || modalType == ActionTypes.MULTIPLEUPDATE) && (
              <span>|</span>
            )}
            <div style={{ color: '#6D6D73' }}>{blockData.blockName}</div>
          </div>
          <div
            style={{
              display: 'flex',
              padding: '25px 0',
              gap: 65,
              fontSize: '14px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                flex: 1,
                width: '50%',
              }}
            >
              <InputComponent
                label="Type"
                input={
                  <OpSelect
                    allowFiltering={true}
                    dataSource={typeList}
                    fields={{ text: 'name', value: 'id' }}
                    filterBarPlaceholder="Search"
                    onChange={(e) => {
                      const args = e.target.itemData;
                      changeType(args);
                      if (modalType === ActionTypes.CREATE || !modalState.disableLinkToggle) {
                        changeForm('series', null);
                        changeForm('seasons', null);
                        changeForm('titleGroup', null);
                      }
                    }}
                    value={
                      typeList.find(
                        (item) => item.name.toLocaleLowerCase() === modalState.type?.toLowerCase(),
                      )?.id
                    }
                    width={250}
                    enabled={!modalState.disableType}
                  />
                }
              />

              <InputComponent
                label="Genre"
                input={
                  <input
                    type="text"
                    defaultValue={modalState.genre}
                    placeholder="Enter a preferred genre"
                    onBlur={(e) => changeForm('genre', e.target.value)}
                    style={{ width: '252px' }}
                    className={`${style.modalInput} ${style.inputWrapper}`}
                    disabled={modalState.disableGenre}
                  />
                }
              />

              <InputComponent
                label="Program MAX Count"
                input={
                  <input
                    type="number"
                    defaultValue={modalState.programMaxCount}
                    min={1}
                    max={9999}
                    onBlur={(e) => changeForm('programMaxCount', e.target.value)}
                    style={{ width: '190px' }}
                    className={`${style.modalInput} ${style.inputWrapper}`}
                    disabled={modalState.disableMaxCount}
                    placeholder="0"
                  />
                }
              />

              <InputComponent
                label="Program Available Duration"
                input={
                  <input
                    type="number"
                    defaultValue={modalState.programAvailableDuration}
                    min={1}
                    max={9999}
                    onBlur={(e) => changeForm('programAvailableDuration', e.target.value)}
                    style={{ width: '190px' }}
                    className={`${style.modalInput} ${style.inputWrapper}`}
                    disabled={modalState.disableAvailableDuration}
                    placeholder=":MM"
                  />
                }
              />

              <InputComponent
                label="Series"
                input={
                  <OpSelect
                    allowFiltering={true}
                    dataSource={seriesList}
                    fields={{ text: 'name', value: 'id' }}
                    filterBarPlaceholder="Search"
                    onChange={(e) => {
                      const args = e.target.itemData;
                      changeForm('series', args);
                      changeForm('seasons', null);
                      changeForm('seasonName', null);
                    }}
                    placeholder={'Please select a preferred series'}
                    value={modalState?.series?.id}
                    width={250}
                    enabled={!modalState.disableSeries}
                  />
                }
              />

              <InputComponent
                label="Seasons"
                input={
                  <OpSelect
                    allowFiltering={true}
                    dataSource={modalState.series ? modalState.series.seasons : []}
                    fields={{ text: 'name', value: 'id' }}
                    filterBarPlaceholder="Search"
                    onChange={(e) => {
                      const args = e.target.itemData;
                      changeForm('seasons', args);
                      changeForm('seasonName', args?.name || null);
                    }}
                    placeholder={'Please select a preferred season'}
                    value={modalState?.seasons?.id}
                    width={250}
                    enabled={!modalState.disableSeason}
                  />
                }
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                flex: 1,
                width: '50%',
              }}
            >
              {(modalType == ActionTypes.UPDATE || modalType === ActionTypes.MULTIPLEUPDATE) && (
                <>
                  <InputComponent
                    label="Day of Week"
                    input={
                      <OpSelect
                        allowFiltering={true}
                        dataSource={daysOfWeek}
                        fields={{ text: 'name', value: 'id' }}
                        filterBarPlaceholder="Search"
                        onChange={(e) => {
                          const args = e.target.itemData;
                          changeForm('dayOfWeek', args);
                        }}
                        value={modalState?.dayOfWeek?.id}
                        width={250}
                        enabled={!modalState.disableDayOfWeek}
                      />
                    }
                  />
                </>
              )}

              <InputComponent
                label="Start Time*"
                input={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                    <div style={{ width: '149px' }}>
                      <OpTimeInput
                        value={modalState.startTime}
                        onChange={(e) => changeForm('startTime', e)}
                        width="full"
                        enabled={
                          (modalType === ActionTypes.CREATE &&
                            Object.keys(recurrencePattern).length !== 0) ||
                          !modalState.disableStartTime
                        }
                        // TASK-5878 PROD ISSUE (13NOV2024): Removing this `disabled` prop
                        // Start Time text field should NEVER be disabled even when linking.
                      />
                    </div>
                    <Switch
                      label="Link"
                      labelStyle={{ color: '#6D6D73' }}
                      text={['No', 'Yes']}
                      active={modalState.hasLink}
                      disabled={modalState.disableLinkToggle}
                      onSwitch={() => {
                        handleToggleLink(!modalState.hasLink);
                      }}
                      blurredOnInactive={false}
                      cssClass={style.switchClass}
                    />
                  </div>
                }
              />

              <InputComponent
                label={'Link Reference*'}
                input={
                  <OpSelect
                    allowFiltering={true}
                    dataSource={referenceList}
                    fields={{ text: 'name', value: 'blockName' }}
                    filterBarPlaceholder="Search"
                    onChange={changeLinkReference}
                    placeholder={'Please select an existing block'}
                    value={modalState?.linkReference?.blockName}
                    width={250}
                    enabled={!(modalState.disableLinkReference || !modalState.hasLink)}
                  />
                }
              />

              <InputComponent
                label={'End Time*'}
                input={
                  <div style={{ width: '250px' }}>
                    <OpTimeInput
                      value={modalState.endTime}
                      onChange={(e) => changeForm('endTime', e)}
                      width="full"
                      enabled={!modalState.disableEndTime}
                    />
                  </div>
                }
              />

              <InputComponent
                label="Placement*"
                input={
                  <OpSelect
                    allowFiltering={true}
                    dataSource={Layouts}
                    fields={{ text: 'name', value: 'id' }}
                    filterBarPlaceholder="Search"
                    onChange={(e) => {
                      const args = e.target.itemData;

                      changeForm('layout', args.name);
                    }}
                    placeholder={modalState.layout}
                    value={
                      Layouts.find(
                        (item) =>
                          item.name.toLocaleLowerCase() === modalState.layout.toLocaleLowerCase(),
                      )?.id
                    }
                    width={250}
                    enabled={!modalState.disablePlacement}
                  />
                }
              />
              {(modalType === ActionTypes.UPDATE ||
                modalType === ActionTypes.CREATE ||
                isModalOfRepeatType) && (
                <InputComponent
                  label={ProgramTypes.TITLEGROUP}
                  input={
                    <OpSelect
                      allowFiltering={true}
                      dataSource={titleGroupList}
                      fields={{ text: 'name', value: 'id' }}
                      filterBarPlaceholder="Search"
                      onChange={(e) => {
                        const args = e.target.itemData;

                        changeForm('titleGroup', args);
                      }}
                      placeholder={'Please select a preferred title group'}
                      value={modalState?.titleGroup?.id}
                      width={250}
                      enabled={!modalState.disableTitleGroup}
                    />
                  }
                />
              )}
            </div>
          </div>
          <hr></hr>
          {/* Recurrence settings */}
          {(modalType == ActionTypes.CREATE || isModalOfRepeatType) && (
            <DailyRecurrencePatternComponent
              modalType={modalType}
              blockType={modalState.type}
              onChangeDays={(days) => {
                setRecurrencePattern((prevState) => ({
                  ...prevState,
                  days,
                }));
              }}
              onChangeRecurrence={(pattern) => {
                setRecurrencePattern(pattern);
              }}
              recurrencePattern={recurrencePattern}
            />
          )}
          <div
            className="errorValidation"
            style={{
              whiteSpace: 'initial',
              width: '100%',
              textAlign: 'right',
              marginTop: '10px',
            }}
          >
            {modalState.errorMessage}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginTop: '12px',
            }}
          >
            <Button defaultBtn text="Cancel" onClick={handleCloseModal} />

            <Button
              text={modalType == ActionTypes.UPDATE ? 'Update' : 'Insert'}
              type="button"
              onClick={() => {
                handleModifyBlock({
                  ...blockData,
                  endTime: modalState.endTime,
                  startTime: modalState.startTime,
                  dayOfWeek: modalState.dayOfWeek,
                  week: 1,
                  genre: modalState.genre,
                  layout: modalState.layout,
                  link: modalState.hasLink,
                  isSequential: modalState.sequential,
                  type: modalState.type,
                  seasonID: modalState.seasons && modalState.seasons.id?.toString(),
                  seasonName: modalState.seasonName,
                  seriesID: modalState.series && modalState.series.id,
                  seriesName: modalState.series && modalState.series.name,
                  titleGroupID: modalState.titleGroup && modalState.titleGroup.id,
                  titleGroupName: modalState.titleGroup && modalState.titleGroup.name,
                });
              }}
            />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
export default ModifyBlock;
